import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronDown, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { requestBrowserPermission, browserPermission } from "@/lib/supportNotify";

const LABEL = { available: "Available", away: "Away", offline: "Offline" };
const DOT = { available: "bg-emerald-500", away: "bg-amber-500", offline: "bg-slate-400" };
const DESC = {
  available: "You are willing to accept Support conversations while active in Top-Recruit.",
  away: "You are temporarily not accepting live chats.",
  offline: "You are not available for live Support.",
};

// Shared Available/Away/Offline control + heartbeat. variant: "header" | "profile".
export function SupportStatusControl({ variant = "header" }) {
  const [me, setMe] = useState(null);
  const [unread, setUnread] = useState(0);
  const [prefs, setPrefs] = useState(null);
  const navigate = useNavigate();
  const beat = useRef();
  useEffect(() => { api.get("/support/me").then((r) => setMe(r.data)).catch(() => setMe({ can_support: false })); }, []);
  useEffect(() => {
    if (!me?.can_support) return;
    api.get("/support/notification-preferences").then((r) => setPrefs(r.data.preferences)).catch(() => {});
    const tick = () => { if (me.status && me.status !== "offline") api.post("/support/heartbeat", { status: me.status }).catch(() => {}); };
    tick();
    beat.current = setInterval(tick, 45000);
    return () => clearInterval(beat.current);
  }, [me?.can_support, me?.status]);
  useEffect(() => {
    if (!me?.can_support) return;
    const load = () => api.get("/support/inbox", { params: { filter: "waiting" } }).then((r) => setUnread((r.data.conversations || []).length)).catch(() => {});
    load(); const t = setInterval(load, 30000); return () => clearInterval(t);
  }, [me?.can_support]);
  if (!me || !me.can_support) return null;
  const set = async (s) => { setMe((m) => ({ ...m, status: s })); try { await api.post("/support/status", { status: s }); } catch (e) {} };
  const savePref = async (patch) => {
    const next = { ...(prefs || {}), ...patch };
    setPrefs(next);
    try { await api.put("/support/notification-preferences", next); } catch (e) {}
  };
  const enableBrowser = async () => {
    const res = await requestBrowserPermission();
    savePref({ browser: res === "granted" });
  };

  if (variant === "profile") {
    const p = prefs || {};
    return (
      <div className="rounded-lg border border-slate-200 p-4" data-testid="profile-support-chat">
        <div className="flex items-center justify-between mb-1">
          <p className="font-semibold text-slate-800 text-sm">Support Chat</p>
          <span className="text-xs rounded-full bg-emerald-50 text-emerald-600 px-2 py-0.5">Access: Enabled</span>
        </div>
        <p className="text-xs text-slate-400 mb-3">Set your live Support availability. Support capability is managed by your administrator.</p>
        <div className="space-y-1.5">
          {["available", "away", "offline"].map((s) => (
            <button key={s} onClick={() => set(s)} data-testid={`support-status-${s}`}
              className={`w-full flex items-start gap-2.5 rounded-lg border p-2.5 text-left transition-colors ${me.status === s ? "border-brand bg-brand/5" : "border-slate-200 hover:bg-slate-50"}`}>
              <span className={`w-2.5 h-2.5 rounded-full mt-1 ${DOT[s]}`} />
              <div><p className="text-sm font-medium text-slate-700">{LABEL[s]}</p><p className="text-[11px] text-slate-400">{DESC[s]}</p></div>
            </button>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Notifications</p>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={p.in_app !== false} onChange={(e) => savePref({ in_app: e.target.checked })} data-testid="notif-in-app" />In-app Support notifications</label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!p.browser} onChange={(e) => e.target.checked ? enableBrowser() : savePref({ browser: false })} data-testid="notif-browser" />
              Browser notifications {browserPermission() === "denied" && <span className="text-[11px] text-rose-400">(blocked in browser)</span>}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!p.sound} onChange={(e) => savePref({ sound: e.target.checked })} data-testid="notif-sound" />Notification sound</label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!p.show_previews} onChange={(e) => savePref({ show_previews: e.target.checked })} data-testid="notif-previews" />Show message previews in notifications</label>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Previews are off by default so sensitive details never appear on lock screens.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      {unread > 0 && (
        <button onClick={() => navigate("/communications?tab=support")} data-testid="support-unread" className="relative p-2 rounded-lg hover:bg-secondary text-slate-500">
          <MessageSquare className="w-[18px] h-[18px]" />
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>
        </button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="support-status-control" className="flex items-center gap-1.5 rounded-lg border border-border bg-white px-2.5 py-1.5 text-sm hover:bg-secondary/60">
            <span className={`w-2.5 h-2.5 rounded-full ${DOT[me.status]}`} />
            <span className="hidden 2xl:block text-slate-700">{LABEL[me.status]}</span>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white w-40">
          {["available", "away", "offline"].map((s) => (
            <DropdownMenuItem key={s} onClick={() => set(s)} data-testid={`support-set-${s}`}>
              <span className={`w-2.5 h-2.5 rounded-full mr-2 ${DOT[s]}`} />{LABEL[s]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
